import { useState } from 'react';
import { Dialog } from '../../components/Dialog.tsx';
import { useEditIssue, useIssueAction } from './api.ts';
import { AssignDialog } from './AssignDialog.tsx';
import { PriorityPicker } from './PriorityPicker.tsx';
import { zoneErrorMessage } from './errors.ts';
import { type IssueDetail, type IssuePriority, type ZoneRoleName } from './types.ts';

type Which = 'assign' | 'resolve' | 'reopen' | 'edit' | null;
const OUTLINED =
  'inline-flex items-center justify-center gap-2 rounded-full border border-[hsl(var(--border-strong))] px-6 py-2.5 text-sm font-medium text-primary transition-colors hover:bg-primary/10 disabled:opacity-50';

/** Dialog with one text field (resolve note / reopen comment). */
function TextActionDialog({
  open, onClose, title, label, required, submitLabel, pending, error, onSubmit,
}: {
  open: boolean; onClose: () => void; title: string; label: string; required: boolean;
  submitLabel: string; pending: boolean; error: string | null; onSubmit: (text: string) => void;
}): JSX.Element | null {
  const [text, setText] = useState('');
  return (
    <Dialog open={open} onClose={onClose} title={title}>
      <form onSubmit={(e) => { e.preventDefault(); onSubmit(text.trim()); }} className="space-y-5">
        <div className="space-y-1.5">
          <label className="field-label" htmlFor="action-text">{label}</label>
          <textarea id="action-text" rows={4} maxLength={2000} required={required} className="textarea text-base" value={text} onChange={(e) => setText(e.target.value)} />
        </div>
        {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={pending || (required && !text.trim())}>
            {pending ? 'Saving…' : submitLabel}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function EditDialog({ issue, open, onClose }: { issue: IssueDetail; open: boolean; onClose: () => void }): JSX.Element | null {
  const edit = useEditIssue();
  const [title, setTitle] = useState(issue.title);
  const [description, setDescription] = useState(issue.description);
  const [priority, setPriority] = useState<IssuePriority>(issue.priority);
  const [locationText, setLocationText] = useState(issue.locationText ?? '');
  const [error, setError] = useState<string | null>(null);

  const submit = (e: React.FormEvent): void => {
    e.preventDefault();
    setError(null);
    edit.mutate(
      {
        id: issue.id,
        patch: { title: title.trim(), description: description.trim(), priority, locationText: locationText.trim() || null },
      },
      { onSuccess: onClose, onError: (err) => setError(zoneErrorMessage(err)) },
    );
  };

  return (
    <Dialog open={open} onClose={onClose} title="Edit issue">
      <form onSubmit={submit} className="space-y-5">
        <div className="space-y-1.5">
          <label className="field-label" htmlFor="edit-title">Title</label>
          <input id="edit-title" required maxLength={140} className="input text-base" value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <label className="field-label" htmlFor="edit-description">Description</label>
          <textarea id="edit-description" required maxLength={4000} rows={5} className="textarea text-base" value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <PriorityPicker value={priority} onChange={setPriority} />
        <div className="space-y-1.5">
          <label className="field-label" htmlFor="edit-location">Location</label>
          <input id="edit-location" maxLength={200} className="input text-base" value={locationText} onChange={(e) => setLocationText(e.target.value)} />
        </div>
        {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={edit.isPending || !title.trim() || !description.trim()}>
            {edit.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/** Shows only what the current user may do; the server remains the authority. */
export function IssueActions({
  issue, userId, zoneRole, layout = 'bar',
}: {
  issue: IssueDetail; userId: string; zoneRole: ZoneRoleName | null;
  /** `bar`: sticky bottom bar on phones. `inline`: a plain button row in the desktop task column. */
  layout?: 'bar' | 'inline';
}): JSX.Element | null {
  const [open, setOpen] = useState<Which>(null);
  const [error, setError] = useState<string | null>(null);
  const start = useIssueAction('start');
  const resolve = useIssueAction('resolve');
  const confirm = useIssueAction('confirm');
  const reopen = useIssueAction('reopen');
  const close = (): void => { setOpen(null); setError(null); };

  const sg = zoneRole === 'SECRETARY_GENERAL';
  // Mirror backend issueWorkflow: the zone role must still match, not just the id.
  const creator = zoneRole === 'COUNCILLOR' && issue.createdById === userId;
  const worker = sg || (zoneRole === 'STAFF' && issue.assigneeId === userId);
  const { status } = issue;

  const canAssign = sg && status !== 'CLOSED';
  const canStart = worker && status === 'ASSIGNED';
  const canResolve = worker && (status === 'ASSIGNED' || status === 'IN_PROGRESS');
  const canConfirm = creator && status === 'RESOLVED';
  const canEdit = creator && status === 'SUBMITTED';
  if (!canAssign && !canStart && !canResolve && !canConfirm && !canEdit) return null;

  const run = (m: ReturnType<typeof useIssueAction>, vars: { note?: string; comment?: string } = {}, then?: () => void): void => {
    setError(null);
    m.mutate({ id: issue.id, ...vars }, { onSuccess: then, onError: (err) => setError(zoneErrorMessage(err)) });
  };

  // Exactly one filled button: the next step in the flow for this user.
  const primary: 'confirm' | 'start' | 'resolve' | 'assign' | 'edit' =
    canConfirm ? 'confirm' : canStart ? 'start' : canResolve ? 'resolve' : canAssign ? 'assign' : 'edit';
  const cls = (k: typeof primary): string => (k === primary ? 'btn-primary' : 'btn-secondary');
  const busy = start.isPending || confirm.isPending;

  return (
    <>
      <div
        className={layout === 'inline'
          ? 'space-y-2'
          : 'sticky bottom-[calc(5.5rem+env(safe-area-inset-bottom))] -mx-4 space-y-2 border-t border-[hsl(var(--border)/0.6)] bg-[hsl(var(--background))] px-4 py-3 sm:-mx-6 sm:px-6'}
      >
        {error && open !== 'resolve' && open !== 'reopen' && <p className="text-sm text-destructive" role="alert">{error}</p>}
        <div className={layout === 'inline' ? 'flex flex-wrap gap-2 [&>button]:flex-none' : 'flex flex-wrap gap-2'}>
          {canConfirm && (
            <>
              <button type="button" className={`${cls('confirm')} flex-1`} disabled={busy} onClick={() => run(confirm)}>
                {confirm.isPending ? 'Saving…' : 'Confirm fixed'}
              </button>
              <button type="button" className={`${OUTLINED} flex-1`} onClick={() => setOpen('reopen')}>Not fixed — reopen</button>
            </>
          )}
          {canStart && (
            <button type="button" className={`${cls('start')} flex-1`} disabled={busy} onClick={() => run(start)}>
              {start.isPending ? 'Saving…' : 'Start work'}
            </button>
          )}
          {canResolve && (
            <button type="button" className={`${cls('resolve')} flex-1`} onClick={() => setOpen('resolve')}>Mark resolved</button>
          )}
          {canAssign && (
            <button type="button" className={`${primary === 'assign' ? 'btn-primary' : OUTLINED} flex-1`} onClick={() => setOpen('assign')}>
              {status === 'SUBMITTED' ? 'Assign' : 'Reassign'}
            </button>
          )}
          {canEdit && (
            <button type="button" className={`${primary === 'edit' ? 'btn-primary' : OUTLINED} flex-1`} onClick={() => setOpen('edit')}>Edit</button>
          )}
        </div>
      </div>

      {canAssign && open === 'assign' && <AssignDialog issue={issue} open onClose={close} />}
      {canEdit && open === 'edit' && <EditDialog issue={issue} open onClose={close} />}
      {canResolve && open === 'resolve' && (
        <TextActionDialog
          open onClose={close} title="Mark resolved" label="Note (optional)" required={false}
          submitLabel="Mark resolved" pending={resolve.isPending} error={error}
          onSubmit={(note) => run(resolve, { note: note || undefined }, close)}
        />
      )}
      {canConfirm && open === 'reopen' && (
        <TextActionDialog
          open onClose={close} title="Reopen issue" label="What is still wrong?" required
          submitLabel="Reopen" pending={reopen.isPending} error={error}
          onSubmit={(comment) => run(reopen, { comment }, close)}
        />
      )}
    </>
  );
}
